import { useQuery } from '@apollo/client';
import { AddCircle, CreateNewFolder, MovieCreation, Sell } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMoveShow } from '~/api/shows';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count, CountSeparator } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import type { ShowsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { assert } from '~/lib/assert';
import { CreateShowWizard, useCreateShowWizard } from '../components/create-show-wizard';
import { ShowListDocument, type ShowList__Show as Show } from '../queries/queries.generated';
import {
  AddFolderDialog,
  BulkActions,
  ShowGrid,
  ShowMoveDocument,
  Table,
  TagsDialog,
} from './components/';
import { FilterAccordion } from './components/FilterAccordion';
import { FilterChips } from './components/FilterChips';
import { BulkTagDialog } from './components/bulk-tag-dialog';
import { ShowFiltersProvider, useShowFiltersParams, useShowFiltersReducer } from './context';

export const ShowList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ showFolderId: string }>();
  const showFolderId = Number(params.showFolderId);
  assert(isFinite(showFolderId), 'showFolderId param is not a legit number');

  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view')?.trim() || 'List';
  searchParams.delete('viewId');

  const handleSearch = useCallback(
    (search: string) => {
      if (!search) return;
      navigate(`../search?search=${search}&backToFolder=${showFolderId}`);
    },
    [navigate, showFolderId],
  );

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const [openNewFolderDialog, setOpenNewFolderDialog] = useState(false);
  const [tagShowDialogOpen, setTagShowDialogOpen] = useState(false);
  const [moveShowDialogOpen, setMoveShowDialogOpen] = useState(false);
  const [showManageTags, setManageTags] = useState(false);
  const showWizard = useCreateShowWizard();
  const [selectedShow, setSelectedShow] = useState<Show>();
  const [moveShow, moveShowInfo] = useMoveShow();

  const filterParams = useShowFiltersParams();
  const tags = filterParams.tags;

  const { orderBy } = useOrderByParams<ShowsOrderBy>();

  const { data, loading } = useQuery(ShowListDocument, {
    variables: {
      condition: { tags },
      currentNetworkId: currentNetwork.id,
      page,
      perPage,
      showFolderId,
      tags,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.showFolder.shows.pageInfo),
  });

  const { data: showMoveData } = useQuery(ShowMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveShowDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      showMoveData?.network?.showFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ?? [],
    [showMoveData],
  );

  const totalCount = useTotalCount(data?.network?.showFolder.shows.pageInfo.nodesCount);

  const shows = data?.network?.showFolder.shows.nodes ?? [];
  const folders = data?.network?.showFolder.children ?? [];

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => setSelectedIds([]), [setSelectedIds]);

  const canDestroySelectedShows = selectedIds.every((selectedId) => {
    const show = shows.find((show) => show.id === selectedId);
    return show?.canDestroy.value;
  });

  const showTags = useMemo(
    () => data?.network?.showTags.map((x) => x.name),
    [data?.network?.showTags],
  );
  const filterCounts = data?.network?.showFilterInfo;

  const [showFiltersState, showFiltersDispatch] = useShowFiltersReducer(filterParams, showTags);

  const filtersActive = useMemo(() => tags.length > 0, [tags]);

  return (
    <ShowFiltersProvider
      {...showFiltersState}
      clearFilters={() => showFiltersDispatch({ type: 'CLEAR_FILTERS' })}
      reinitialize={(params) => showFiltersDispatch({ type: 'REINITIALIZE', params })}
      setFilter={(value) => showFiltersDispatch({ type: 'SET_FILTER', ...value })}
      toggleFilter={(field) => showFiltersDispatch({ type: 'TOGGLE_FILTER', field })}
      togglePanel={() => showFiltersDispatch({ type: 'TOGGLE_PANEL' })}
    >
      <Toolbar
        titleIcon={<MovieCreation />}
        titleText={t('common.tabs.shows')}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setManageTags(!showManageTags)}
              startIcon={<Sell />}
              variant="outlined"
            >
              {t('common.tags.manage_tags')}
            </Button>
            <Button
              disabled={!data?.network?.showFolder.canUpdate.value}
              onClick={() => setOpenNewFolderDialog((prev: boolean) => !prev)}
              startIcon={<CreateNewFolder />}
              variant="outlined"
            >
              {t('pages.shows.buttons.create_folder')}
            </Button>
            <Button
              color="primary"
              disabled={!currentNetwork.canCreateShows.value}
              onClick={showWizard.open}
              startIcon={<AddCircle />}
              variant="contained"
            >
              {t('pages.shows.buttons.new_show')}
            </Button>
          </Box>
        }
        breadcrumbsLabel={
          data?.network?.showFolder && (
            <FolderBreadcrumbs
              allItems={() =>
                navigate({ pathname: `../${folderTree.id}`, search: location.search })
              }
              label={t('pages.shows.all_shows_label')}
              changeFolder={(id) => navigate({ pathname: `../${id}`, search: location.search })}
              folder={data.network.showFolder}
            />
          )
        }
      />

      <ListPageContainer>
        <FilterSidebar open={showFiltersState.panel.open} persistAs="showList:filterWidth">
          <FilterAccordion filterCounts={filterCounts} />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => showFiltersDispatch({ type: 'TOGGLE_PANEL' })}
                />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.shows.search_placeholder')}
                  search=""
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions
                    selectedIds={selectedIds}
                    onTagShow={() => setTagShowDialogOpen(true)}
                    onMoveShow={() => setMoveShowDialogOpen(true)}
                    canDestroySelectedShows={canDestroySelectedShows}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 1,
                    }}
                  >
                    <Count
                      selectedCount={0}
                      totalCount={data?.network?.showFolder.children.length || 0}
                      thing="common.pluralize.folder_plural"
                    />
                    <CountSeparator />
                  </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount || 0}
                    thing="common.pluralize.show_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips />
              </Box>
              <ViewToggle
                view={view as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <Table
                    loading={loading}
                    shows={shows}
                    folders={folders}
                    selectedShow={selectedShow}
                    onCheck={setSelectedIds}
                    onSelectedShow={setSelectedShow}
                    selectedIds={selectedIds}
                  />
                ) : (
                  <ShowGrid
                    shows={shows}
                    folders={folders}
                    loading={loading}
                    selectedShow={selectedShow}
                    onCheck={setSelectedIds}
                    onSelectedShow={setSelectedShow}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>

          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await moveShow({
                refetchQueries: ['ShowList'],
                variables: {
                  input: {
                    showFolderId: targetFolderId,
                    showIds: selectedIds as number[],
                  },
                },
              });
              setSelectedIds([]);
            }}
            loading={moveShowInfo.loading}
            onClose={() => setMoveShowDialogOpen(false)}
            open={moveShowDialogOpen}
            i18nKey="pages.shows.list.dialogs.move_show.title"
          />

          <AddFolderDialog
            showFolderId={showFolderId}
            openNewFolderDialog={openNewFolderDialog}
            setOpenNewFolderDialog={setOpenNewFolderDialog}
          />
          {showManageTags && <TagsDialog onClose={() => setManageTags(false)} open />}

          {tagShowDialogOpen && (
            <BulkTagDialog
              showIds={selectedIds as string[]}
              showTags={data?.network?.showTags.map((x) => x.name) || []}
              onClose={() => setTagShowDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
              tags={tags}
            />
          )}
        </ListContainer>
      </ListPageContainer>
      <CreateShowWizard {...showWizard.props} />
    </ShowFiltersProvider>
  );
};
